import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Gift, Heart, Share2, Camera, Loader2, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  getDgPointsBalance, getDgPointsHistory, submitSocialClaim,
  listFavoriteTrainers, getMyReferral,
} from '../lib/api';

const CLAIM_ACTIONS = [
  { id: 'follow_instagram', label: 'Follow on Instagram', points: 50, hint: 'Follow @DojoDash on Instagram, then upload a screenshot.' },
  { id: 'follow_tiktok', label: 'Follow on TikTok', points: 50, hint: 'Follow @DojoDash on TikTok, then upload a screenshot.' },
  { id: 'follow_youtube', label: 'Follow on YouTube', points: 50, hint: 'Subscribe on YouTube, then upload a screenshot.' },
  { id: 'follow_all', label: 'Follow all 3 platforms', points: 200, hint: 'Bonus: upload a screenshot showing all three follows.' },
  { id: 'engagement', label: 'Engage on a post', points: 10, hint: 'Like / comment on a DojoDash post, then upload a screenshot.' },
  { id: 'tag_post', label: 'Post & tag DojoDash', points: 150, hint: 'Post about DojoDash and tag us, then upload the screenshot.' },
];

const RewardsPanel = () => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [referral, setReferral] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimAction, setClaimAction] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [b, h, f, r] = await Promise.all([
        getDgPointsBalance(), getDgPointsHistory(), listFavoriteTrainers(), getMyReferral(),
      ]);
      setBalance(b.data?.balance || 0);
      setHistory(h.data || []);
      setFavorites(f.data || []);
      setReferral(r.data || null);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const openClaim = (action) => { setClaimAction(action); setScreenshot(null); setClaimOpen(true); };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_400_000) { toast.error('Image must be under 1.4MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(file);
  };

  const submitClaim = async () => {
    if (!claimAction || !screenshot) { toast.error('Upload a screenshot'); return; }
    setSubmitting(true);
    try {
      await submitSocialClaim(claimAction.id, screenshot);
      toast.success('Claim submitted, pending review.');
      setClaimOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const referralUrl = referral?.code ? `${window.location.origin}/register?ref=${referral.code}` : '';
  const copyReferral = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Copy failed'); }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6 mb-8" data-testid="rewards-panel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#C0A062]/10 border border-[#C0A062]/40 flex items-center justify-center">
            <Gift className="w-5 h-5 text-[#C0A062]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">DG Points</p>
            <p className="text-3xl font-bold text-white" data-testid="dgpoints-balance">{balance.toLocaleString()}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" onClick={() => setClaimOpen(true)} data-testid="open-claim-btn">
            <Camera className="w-4 h-4 mr-1" /> Claim Points
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Earn history */}
        <div className="bg-[#0d0d10] rounded-md border border-zinc-800 p-4">
          <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-3">Recent activity</p>
          {history.length === 0 ? (
            <p className="text-sm text-zinc-500">No activity yet. Complete a session or claim a social bonus.</p>
          ) : (
            <ul className="space-y-2 max-h-40 overflow-y-auto" data-testid="dgpoints-history">
              {history.slice(0, 8).map((h) => (
                <li key={h.id} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-300 truncate">{h.reason.replace(/_/g, ' ')}</span>
                  <span className="text-[#C0A062] font-semibold shrink-0">+{h.points}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Referral */}
        <div className="bg-[#0d0d10] rounded-md border border-zinc-800 p-4" data-testid="referral-block">
          <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-3">Invite a friend</p>
          {referral?.code ? (
            <>
              <p className="text-sm text-zinc-300 mb-2">Earn 200 pts when a friend completes their first session.</p>
              <div className="flex gap-2">
                <Input value={referralUrl} readOnly className="bg-[#09090B] border-zinc-700 text-white text-xs" data-testid="referral-link" />
                <Button size="sm" onClick={copyReferral} className="bg-[#C0A062] text-black hover:bg-[#a98a52]" data-testid="copy-referral-btn">
                  {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-zinc-500 mt-2">{referral.referred_count} signed up</p>
            </>
          ) : (
            <p className="text-sm text-zinc-500">Loading link…</p>
          )}
        </div>

        {/* Favorites */}
        <div className="bg-[#0d0d10] rounded-md border border-zinc-800 p-4" data-testid="favorites-block">
          <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-3">My Coaches</p>
          {favorites.length === 0 ? (
            <div>
              <p className="text-sm text-zinc-500 mb-3">Save coaches you love. Rebook in one tap.</p>
              <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" onClick={() => navigate('/find-trainer')} data-testid="browse-coaches-btn">
                Browse coaches
              </Button>
            </div>
          ) : (
            <ul className="space-y-2 max-h-40 overflow-y-auto" data-testid="favorites-list">
              {favorites.map((t) => (
                <li key={t.id} className="flex items-center justify-between text-sm">
                  <Link to={`/trainers/${t.id}`} className="text-zinc-300 hover:text-[#C0A062] truncate" data-testid={`favorite-link-${t.id}`}>
                    <Heart className="w-3.5 h-3.5 inline text-[#C0A062] mr-1.5" />
                    {t.full_name}
                  </Link>
                  <Button size="sm" variant="ghost" className="text-[#C0A062] hover:bg-[#C0A062]/10 h-7 px-2 text-xs" onClick={() => navigate(`/trainers/${t.id}`)} data-testid={`favorite-book-${t.id}`}>
                    Book
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Claim modal */}
      <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
        <DialogContent className="bg-[#0d0d10] border-zinc-800 text-white max-w-md" data-testid="claim-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#C0A062]" /> Claim Points
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Pick an action. Upload a screenshot. Admin will review and credit your points.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-60 overflow-y-auto">
            {CLAIM_ACTIONS.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setClaimAction(a)}
                className={`w-full flex items-center justify-between p-3 rounded-md border text-left transition-colors ${
                  claimAction?.id === a.id
                    ? 'border-[#C0A062] bg-[#C0A062]/10'
                    : 'border-zinc-800 hover:border-zinc-600'
                }`}
                data-testid={`claim-action-${a.id}`}
              >
                <span className="text-sm">{a.label}</span>
                <span className="text-[#C0A062] text-sm font-semibold">+{a.points}</span>
              </button>
            ))}
          </div>

          {claimAction && (
            <div className="space-y-3 mt-4 pt-4 border-t border-zinc-800">
              <p className="text-xs text-zinc-400">{claimAction.hint}</p>
              <input
                type="file"
                accept="image/*"
                onChange={onFileChange}
                className="block w-full text-sm text-zinc-400 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#C0A062] file:text-black file:font-semibold hover:file:bg-[#a98a52]"
                data-testid="claim-screenshot-input"
              />
              {screenshot && (
                <img src={screenshot} alt="Screenshot preview" className="w-full max-h-40 object-contain rounded border border-zinc-800" />
              )}
              <Button
                onClick={submitClaim}
                disabled={!screenshot || submitting}
                className="w-full bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
                data-testid="submit-claim-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Submit for review
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RewardsPanel;
